import messages from "i18n/messages";
import first from "lodash/first";
import truncate from "lodash/truncate";
import flowRight from "lodash/flowRight";
import { makeSelectCurOrg } from "logic/orgs";
import { withRouter } from "next/router";
import { makeSelectCustomer } from "logic/customers";
import {
  calcDeliveryCosts,
  calcFees,
  calcSubtotal,
  calcTotal,
  checkVoucherCode,
  checkPromoCode,
  makeSelectLoading,
  makeSelectCodeError,
  makeSelectAppliedDiscountCampaign,
  makeSelectAppliedVoucher,
} from "logic/payment";
import { makeSelectDateById, extractDateId } from "logic/dates";
import PropTypes from "prop-types";
import { Component } from "react";
import { FormattedMessage, FormattedNumber, injectIntl } from "react-intl";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { createStructuredSelector } from "reselect";
import { Currency, ExpiresAtCountdown, VoucherOrDiscount } from "components";
import SimplExchange from "./SimplExchange";

const SummaryRowItem = (props) =>
  props.item.qty ? <SummaryOrderItem {...props} /> : <OldRowItem {...props} />;

const OldRowItem = ({ item, org }) => (
  <tr>
    <td>{item.description}</td>
    <td className="text-right">
      <Currency value={item.value * item.amount} />
    </td>
  </tr>
);

const SummaryOrderItem = ({ item, org }) => (
  <tr>
    <td>
      {item.qty} {truncate(item.title, { length: 25 })}
    </td>
    <td className="text-right">
      <Currency value={item.total} />
    </td>
  </tr>
);

const SummarySeatsRow = ({ seats, org }) => (
  <tr>
    <td>
      {seats.length}&nbsp;
      <FormattedMessage {...messages.tickets} />
    </td>
    <td className="text-right">
      <FormattedNumber
        style={`currency`}
        currency={org.currency}
        value={calcSubtotal(seats) / 100.0}
      />
    </td>
  </tr>
);

const TimeLeft = ({ payable }) => (
  <div className="time-left">
    <div className="exp-time">
      <i className="tt-icon tt-icon-clock" />
      <ExpiresAtCountdown date={payable.expires_at} />
    </div>
    <div className="exp-time-desc">
      <FormattedMessage {...messages.yourTicketsAreGuaranteedForThisTime} />
    </div>
  </div>
);

const ShippingAddr = ({ contact, addr }) => (
  <div className="contact-summary">
    <h5>
      <FormattedMessage {...messages.deliveryAddr} />
    </h5>
    {addr.street ? (
      <div>
        <div className="name">
          {contact.firstname} {contact.lastname}
        </div>
        <div className="addr">
          {addr.street}
          <br />
          {addr.postcode} {addr.city}
        </div>
      </div>
    ) : (
      <div className="alert alert-danger">
        <FormattedMessage {...messages.noShippingAddrGiven} />
      </div>
    )}
  </div>
);

const BillingAddr = ({ contact, donation }) => (
  <div className="contact-summary">
    <h5>
      <FormattedMessage
        {...messages[donation ? "receiptAddr" : "billingAddr"]}
      />
    </h5>
    {contact.street ? (
      <div>
        <div className="name">
          {contact.firstname} {contact.lastname}
        </div>
        <div className="addr">
          {contact.street}
          <br />
          {contact.postcode} {contact.city}
        </div>
      </div>
    ) : (
      <div className="alert alert-warning">
        <FormattedMessage
          {...messages[donation ? "noReceiptAddrGiven" : "noBillingAddrGiven"]}
        />
      </div>
    )}
  </div>
);

class PaymentShortSummary extends Component {
  render() {
    const {
      org,
      values,
      donation,
      paymentData,
      deliveryMethod,
      payable,
      date,
      appliedVoucher,
      appliedDiscountCampaign,
    } = this.props;
    if (!values) return <></>;

    const subtotal = calcSubtotal(values);
    const total = calcTotal(values);
    const deliveryCosts = calcDeliveryCosts(
      org,
      deliveryMethod,
      first(values).description
    );
    const fees = calcFees(subtotal, org, values.length);
    const discountRate =
      appliedDiscountCampaign && appliedDiscountCampaign.value
        ? appliedDiscountCampaign.value / 10000
        : 0;
    const overallTotal = total + deliveryCosts + fees;
    const discount = overallTotal * discountRate;
    let voucherValue = 0;
    if (appliedVoucher)
      voucherValue =
        -1 *
        (appliedVoucher.remaining_value > overallTotal
          ? overallTotal
          : appliedVoucher.remaining_value);
    return (
      <div className="payment-summary payment-short-summary">
        <h2>
          <FormattedMessage {...messages.summary} />
        </h2>
        <table className="table">
          <thead>
            <tr>
              <th>
                <FormattedMessage {...messages.description} />
              </th>
              <th className="text-right">
                <FormattedMessage {...messages.totalPrice} /> [{org.currency}]
              </th>
            </tr>
          </thead>
          <tbody>
            {first(values).description || first(values).qty ? (
              values.map((item, index) => (
                <SummaryRowItem key={`item_${index}`} item={item} org={org} />
              ))
            ) : (
              <SummarySeatsRow seats={values} org={org} />
            )}
          </tbody>
          <tfoot>
            {appliedDiscountCampaign ? (
              <tr>
                <td>{appliedDiscountCampaign.name}</td>
                <td className="text-right">
                  <FormattedNumber
                    style={`percent`}
                    value={appliedDiscountCampaign.value / 10000}
                  />
                </td>
              </tr>
            ) : null}
            {voucherValue < 0 ? (
              <tr>
                <td className="font-weight-bold text-success">
                  <FormattedMessage {...messages.applyVoucher} />
                </td>
                <td className="text-right font-weight-bold text-success">
                  <FormattedNumber
                    style={`currency`}
                    currency={org.currency}
                    value={voucherValue / 100.0}
                  />
                </td>
              </tr>
            ) : null}
            {deliveryMethod === "post" ? (
              <tr>
                <td>
                  <FormattedMessage {...messages.shipping} />
                </td>
                <td className="text-right">
                  <FormattedNumber
                    style={`currency`}
                    currency={org.currency}
                    value={deliveryCosts / 100.0}
                  />
                </td>
              </tr>
            ) : null}
            {fees > 0 ? (
              <tr>
                <td>
                  <FormattedMessage {...messages.fees} />
                </td>
                <td className="text-right">
                  <FormattedNumber
                    style={`currency`}
                    currency={org.currency}
                    value={fees / 100.0}
                  />
                </td>
              </tr>
            ) : null}
            {/* <tr>
              <td></td>
              <td><FormattedMessage {...messages.vat} /></td>
              <td>{isNaN(vat) || vat === 0 ? <FormattedMessage {...messages.vatFreeNGO} /> : ''}</td>
              <td className="text-right"><FormattedNumber style={`currency`} currency={org.currency} value={vat / 100.0} /></td>
            </tr> */}
            <tr className="total">
              <td>
                <FormattedMessage {...messages.total} />
                <p className="small text-muted">
                  <FormattedMessage {...messages.inclVat} />
                </p>
              </td>
              <td className="text-right">
                {payable && payable.id.includes("ord4_") ? (
                  <Currency value={payable.total} />
                ) : (
                  <Currency
                    value={
                      total + deliveryCosts + fees - discount + voucherValue
                    }
                  />
                )}
              </td>
            </tr>
          </tfoot>
        </table>
        <div className="d-none-xs d-block-sm">
          {((org.portal && org.portal.vouchers_enabled) ||
            (date && date.discounts_enabled)) &&
          payable.id.includes("ord_") &&
          !payable.voucher_id ? (
            <VoucherOrDiscount
              {...this.props.actions}
              loading={this.props.loading}
              codeError={this.props.codeError}
            />
          ) : null}
        </div>
        {payable && payable.expires_at ? <TimeLeft payable={payable} /> : null}
        {this.props.contact ? (
          <BillingAddr donation={donation} contact={this.props.contact} />
        ) : null}
        {(deliveryMethod === "post" || deliveryMethod === "bicycle") &&
        this.props.contact &&
        this.props.contact.addresses.length > 0 ? (
          <ShippingAddr
            contact={this.props.contact}
            addr={
              this.props.contact.addresses.length > 0
                ? this.props.contact.addresses[0]
                : this.props.contact
            }
          />
        ) : (
          <div className="contact-summary">
            <FormattedMessage {...messages.shippingToBillingAddr} />
          </div>
        )}
      </div>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  org: makeSelectCurOrg(),
  date: makeSelectDateById((props) => extractDateId(props)),
  codeError: makeSelectCodeError(),
  appliedDiscountCampaign: makeSelectAppliedDiscountCampaign(),
  appliedVoucher: makeSelectAppliedVoucher(),
  contact: makeSelectCustomer(),
  loading: makeSelectLoading(),
});

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(
    {
      checkVoucherCode,
      checkPromoCode,
    },
    dispatch
  ),
});

const enhance = flowRight(
  injectIntl,
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
);

PaymentShortSummary.propTypes = {
  values: PropTypes.array,
  deliveryMethod: PropTypes.string,
};

export default enhance(PaymentShortSummary);
